import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, createStyles, makeStyles, MenuItem, MenuList, Popover, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import StaffMemberT, { PrimarySpecialty } from '@/types/StaffMember';
import Spinner from '@/se/components/Spinner';
import StaffMember from './StaffMember';
import { useStaffMembersSubscription } from './StaffShiftContext';
import { useStaffSlotContext } from './StaffSlotContext';
import StaffShiftT from '@/types/StaffShift';
import { Edit, RemoveCircleOutline } from '@material-ui/icons';
import OpenPositionForm from './OpenPositionForm';
import { Procedure, ShiftState } from '@/types/Procedure';
import { useScope } from '@/hooks/useScope';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import IconButton from '@material-ui/core/IconButton';
import OpenPositionNotification from './OpenPositionNotification';
import { StaffShiftStoreContext, useCreateStaffShiftStore } from './StaffShiftChip';
import PrimarySpecialtyPicker from './PrimarySpecialtyPicker';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

interface StaffEmptySlotMenuItemProps {
  staffMember: StaffMemberT;
  toggleStaffMember: (staffMember: StaffMemberT) => (event: React.MouseEvent<HTMLElement>) => Promise<void>;
  type: 'RoomShift' | 'ProcedureShift';
}

const StaffEmptySlotMenuItem: FC<StaffEmptySlotMenuItemProps> = ({ staffMember, toggleStaffMember, type }) => {
  const classes = useStyles();

  const staffShiftStore = useCreateStaffShiftStore();

  return (
    <MenuItem key={staffMember.id} onClick={toggleStaffMember(staffMember)}>
      <StaffShiftStoreContext.Provider value={staffShiftStore}>
        <StaffMember
          {...staffMember}
          isMenuListItem
          interactive
          className={classes.staffMember}
          shiftState={staffMember.assignedShift?.shiftState ?? ShiftState.Available}
          type={type}
        />
      </StaffShiftStoreContext.Provider>
    </MenuItem>
  );
};

interface StaffSlotMenuProps extends StaffShiftT {
  procedure?: Procedure;
  type?: 'RoomShift' | 'ProcedureShift';
  children: (onClick: (event: React.MouseEvent<HTMLElement>) => void) => ReactElement<any, any> | null;
}

const StaffEmptySlotMenu: FC<StaffSlotMenuProps> = ({
  id,
  from,
  to,
  procedure,
  openPosition,
  staff,
  type,
  children,
}) => {
  const classes = useStyles();

  const searchEl = React.useRef<HTMLInputElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? 'simple-popover' : undefined;
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState('');

  const {
    activateSubscription,
    staffMembers,
    loading,
    setPrimarySpecialty: setContextPrimarySpecialty,
  } = useStaffMembersSubscription();

  const filteredStaffMembers = useMemo(() => {
    const chunks = search.toLowerCase().split(/\s+/);
    return (staffMembers || []).filter((staffMember: StaffMemberT) => {
      const staffMemberName = staffMember.name.toLowerCase();
      return chunks.every(chunk => staffMemberName.indexOf(chunk) >= 0);
    });
  }, [search, staffMembers]);

  const availableStaffMembers = useMemo(
    () => filteredStaffMembers.filter(_ => !_.assignedShift),
    [filteredStaffMembers]
  );

  useEffect(() => {
    if (open) {
      searchEl.current?.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: 0, align: 'start' });
    });
  }, [open, availableStaffMembers.length]);

  const handleSearchUpdate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  }, []);

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const staffSlotContext = useStaffSlotContext();
  const scope = useScope();

  const [primarySpecialty, setPrimarySpecialty] = useState<PrimarySpecialty | null | undefined>(PrimarySpecialty.OR);

  const toggleStaffMember = useCallback(
    (staffMember: StaffMemberT) => async (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      await staffSlotContext.assign(id, staffMember?.staffId);

      handleClose();
    },
    [handleClose, id, staffSlotContext]
  );

  const removeSlot = useCallback(async () => {
    await staffSlotContext.remove(id, scope?.hospital.id, scope?.hospital.id);
    handleClose();
  }, [handleClose, id, scope?.hospital.id, staffSlotContext]);

  const startEditingSlot = useCallback(() => {
    staffSlotContext.openOpenPositionDialog();
    handleClose();
  }, [handleClose, staffSlotContext]);

  return (
    <>
      {children(handleButtonClick)}
      <OpenPositionForm id={id} from={from} to={to} openPosition={openPosition} />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClick={e => e.stopPropagation()}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {primarySpecialty !== undefined && (
          <PrimarySpecialtyPicker
            value={primarySpecialty}
            onChange={ps => {
              setPrimarySpecialty(ps);
              setContextPrimarySpecialty(ps);
              activateSubscription({ primarySpecialty: ps });
            }}
          />
        )}
        {staffMembers ? (
          <Box className={classes.root}>
            <TextField
              size="small"
              variant="outlined"
              value={search}
              onChange={handleSearchUpdate}
              inputRef={searchEl}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" className={classes.searchIcon} />,
                autoCapitalize: 'off',
                autoComplete: 'off',
                autoCorrect: 'off',
                classes: {
                  root: classes.inputRoot,
                  inputAdornedStart: classes.inputAdornedStart,
                },
              }}
              classes={{
                root: classes.textFieldRoot,
              }}
            />
            <div className={classes.virtualListContainer} onClick={e => e.stopPropagation()}>
              <Virtuoso
                ref={virtuosoRef}
                style={{ height: '26rem', width: '100%' }}
                totalCount={availableStaffMembers.length}
                defaultItemHeight={56}
                computeItemKey={index => availableStaffMembers[index]?.id ?? `staff-${index}`}
                itemContent={index => {
                  const staffMember = availableStaffMembers[index];

                  return (
                    <StaffEmptySlotMenuItem
                      key={staffMember?.id}
                      staffMember={staffMember}
                      toggleStaffMember={toggleStaffMember}
                      type={type ?? 'RoomShift'}
                    />
                  );
                }}
              />
            </div>
            <MenuList onClick={e => e.stopPropagation()} className={classes.menuListActions}>
              <MenuItem key="button-edit" onClick={startEditingSlot}>
                <Chip
                  icon={<Edit className={classes.edit} />}
                  label={'Edit Slot'}
                  size="small"
                  className={classes.removeChip}
                />
              </MenuItem>
              <MenuItem key="button-rm" onClick={removeSlot}>
                <Chip
                  icon={<RemoveCircleOutline className={classes.remove} />}
                  label={'Remove Slot'}
                  size="small"
                  className={classes.removeChip}
                />
              </MenuItem>
              <MenuItem key="button-notify" onClick={() => setOpenModal(true)}>
                <Chip
                  icon={
                    <IconButton>
                      <NotificationsActiveIcon color={'primary'} />
                    </IconButton>
                  }
                  label={'Notify'}
                  size="small"
                />
              </MenuItem>
            </MenuList>
            {openModal && (
              <OpenPositionNotification
                shiftId={id}
                open={openModal}
                handleClickOpen={() => setOpenModal(true)}
                handleClickClose={() => setOpenModal(false)}
              />
            )}
          </Box>
        ) : (
          <div className={classes.spinnerContainer}>
            <Spinner freezeAnimation={!open || !loading} className={classes.spinner} />
          </div>
        )}
      </Popover>
    </>
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      maxHeight: '38rem',
      overflow: 'hidden',
    },
    virtualListContainer: {
      width: '100%',
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
    },
    menuListActions: {
      borderTop: `1px solid rgba(127, 127, 127, 0.2)`,
      position: 'sticky',
      backgroundColor: 'inherit',
      bottom: 0,
      zIndex: 500,
    },
    textFieldRoot: {
      margin: '1rem',
      marginBottom: 0,
      padding: 0,
    },
    inputRoot: {
      width: '14rem',
      fontSize: '0.8125em',
      padding: '0.25em',
    },
    searchIcon: {
      marginLeft: '0.25em',
      marginRight: '0.25em',
    },
    inputAdornedStart: {
      paddingLeft: 0,
    },
    spinnerContainer: {
      padding: '1rem',
    },
    spinner: {},
    remove: {
      color: 'red',
    },
    edit: {
      color: theme.palette.primary.light,
    },
    staffMember: {
      flex: 1,
    },
    removeChip: {
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
  })
);

export default StaffEmptySlotMenu;
